import type { CaseStudy } from '../types';

export const mixue: CaseStudy = {
  id: 'mixue',
  order: 12,
  title: '蜜雪冰城',
  english: 'Mixue',
  category: 'business',
  era: '1997 — 今',
  region: '中国 / 全球',
  accent: '#c65a3e',
  hook: '它看起来卖的是几块钱的柠檬水和冰淇淋，真正撑起规模的是供应链、加盟密度和一套让总部与门店“多卖货”绑定的收入结构。',
  question: '低价为什么没有把它拖垮，反而成了扩张的引擎？',
  summary:
    '蜜雪冰城的低价建立在极简菜单、规模化采购、自建供应链和高密度加盟之上。总部收入很大一部分来自向加盟店销售原料、设备与包装，这使扩张与单店采购强相关，也放大了饱和与食品安全的治理难度。',
  thesis: '低价不是策略的全部；真正的结构是「低毛利产品 × 高密度网络 × 上游供应链利润」。',
  quote: '当总部靠向门店卖货赚钱，扩张就会比单店利润更容易被优先考虑。',
  isNew: true,
  entryNodeIds: ['mixue-lowprice', 'mixue-franchise', 'mixue-supplychain'],
  focusNodeId: 'mixue-supplychain',
  lensIds: ['scale', 'standards', 'incentives', 'scarcity'],
  opportunityIds: ['opp-small-format-supply'],
  sourceIds: ['mixue-prospectus'],
  facts: [
    {
      text: '蜜雪冰城 1997 年创立于郑州，以现制茶饮、冰淇淋等低价产品为主，门店主要采取加盟模式。',
      sourceIds: ['mixue-prospectus'],
    },
    {
      text: '截至 2024 年，品牌门店数已超过 4 万家，绝大多数为加盟门店；这一规模使其成为全球门店数最多的现制饮品品牌之一。',
      sourceIds: ['mixue-prospectus'],
    },
    {
      text: '招股书披露，公司收入很大一部分来自向加盟门店销售商品与设备（原料、包装、设备等），而非单纯依赖加盟费或权利金。',
      sourceIds: ['mixue-prospectus'],
    },
  ],
  nodes: [
    {
      id: 'mixue',
      caseId: 'mixue',
      kind: 'case',
      stage: 0,
      lane: 0,
      title: '蜜雪冰城',
      summary: '用低价、加盟密度和供应链做出数万家门店。',
      detail:
        '蜜雪冰城的产品价格带低、菜单相对简单、门店面积小，适合在高密度人群中快速复制。总部通过加盟扩张网络，再通过向门店供应原料、包装和设备获得收入。低价与规模不是二选一，而是互相依赖。',
      boundary: '本案例聚焦现制饮品与加盟供应链，不适用于所有低价零售；不同市场价格与竞争结构差异很大。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['mixue-prospectus'],
    },
    {
      id: 'mixue-lowprice',
      caseId: 'mixue',
      kind: 'mechanism',
      stage: 1,
      lane: -1,
      title: '低价锚点：把尝试成本降到很低',
      summary: '几元钱的产品降低了消费门槛，也形成“日常、高频、可分享”的购买场景。',
      detail:
        '低价不是单纯让利，而是改变消费频率与决策方式：用户不必为一次尝试做太多比较，门店可以用高周转覆盖低单价。要让低价可持续，必须把原料、人工、租金和损耗压到极低，并保持足够客流。',
      boundary: '低价策略在租金高、客流不足或原料成本上涨时很容易失效。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['mixue-prospectus'],
    },
    {
      id: 'mixue-franchise',
      caseId: 'mixue',
      kind: 'mechanism',
      stage: 1,
      lane: 1,
      title: '加盟扩张：用社会资本开门店',
      summary: '加盟者承担开店资本与日常经营，总部以供应链和品牌系统扩张。',
      detail:
        '加盟模式让小店快速铺开，总部不必为每个门店投入全部资本。与只收权利金的模式不同，蜜雪冰城通过向门店销售原料与设备获得收入，总部扩张与门店采购量直接相关。',
      boundary: '当门店密度过高、单店收入下降，加盟者的回本周期会拉长，扩张的可持续性受挑战。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['mixue-prospectus'],
    },
    {
      id: 'mixue-supplychain',
      caseId: 'mixue',
      kind: 'mechanism',
      stage: 2,
      lane: -1,
      title: '供应链利润：总部卖货给门店',
      summary: '自建采购、生产与物流，把原料、包装和设备变成总部的主要收入来源。',
      detail:
        '当门店数量足够大，总部可以集中采购、自己生产关键原料、建设仓储物流，降低单位成本并向加盟店销售。低价产品因此仍有利润空间。代价是总部的收入更依赖门店持续采购，而非单店利润本身。',
      boundary: '供应链重资产需要高产能利用率；门店增长放缓时，工厂与仓储的固定成本会反过来压利润。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['mixue-prospectus'],
    },
    {
      id: 'mixue-density',
      caseId: 'mixue',
      kind: 'mechanism',
      stage: 2,
      lane: 1,
      title: '密度：品牌、物流与选址的复利',
      summary: '同一城市门店越密，物流、营销与消费者认知的成本越低，但也会互相分流。',
      detail:
        '高密度让配送线路更经济、品牌曝光更频繁、消费者更容易“顺手买”。但密度有临界点：新店会分走老店客流，加盟者之间的竞争加剧。总部与单店的利益在这一刻出现分歧。',
      boundary: '人口密度、动线、消费习惯和竞争格局决定承载量；不是越密越好。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['mixue-prospectus'],
    },
    {
      id: 'mixue-condition',
      caseId: 'mixue',
      kind: 'condition',
      stage: 2,
      lane: 0,
      title: '前提：下沉市场与供应链集群',
      summary: '广阔的低线城市、密集的消费场景和中国成熟的食品供应链，支撑低价高频模型。',
      detail:
        '低线城市租金与人工相对低，年轻人对现制饮品的日常需求增长；中国成熟的原料、包装、设备与物流供应链，让小店可以低成本获得稳定供给。没有这些条件，低价与加盟密度很难同时成立。',
      boundary: '海外市场的供应链、租金、劳动法与消费习惯不同，复制速度与单位经济会变化。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['mixue-prospectus'],
    },
    {
      id: 'mixue-outcome',
      caseId: 'mixue',
      kind: 'outcome',
      stage: 3,
      lane: 0,
      title: '结果：规模、低价与供应链收入',
      summary: '数万家门店形成采购规模，总部通过供应链获得收入，品牌获得低价认知。',
      detail:
        '这套结构让蜜雪冰城在低价带形成规模优势：采购量、生产能力与物流密度共同压低成本；门店网络又强化品牌与选址能力。它卖的既是饮品，也是“让一个小店能低成本卖饮品”的系统。',
      boundary: '规模优势不等于单店一定赚钱；加盟者的回报取决于选址、竞争、租金与运营。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['mixue-prospectus'],
    },
    {
      id: 'mixue-risk',
      caseId: 'mixue',
      kind: 'risk',
      stage: 4,
      lane: -1,
      title: '脆弱处：密度、食安与加盟者回报',
      summary: '扩张越快，门店分流、食品安全与加盟者利润的压力越集中。',
      detail:
        '高密度可能让单店收入下降，加盟者回本变慢；数万家门店的食品安全与操作规范，需要持续监督；总部收入与门店采购绑定，也可能让扩张优先于单店健康。这些风险不会立刻出现在营收里，但会在品牌信任上累积。',
      boundary: '强监督、数字化溯源、合理选址与加盟者筛选可以降低风险，但不能消除规模带来的治理难度。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['mixue-prospectus'],
    },
    {
      id: 'mixue-opp',
      caseId: 'mixue',
      kind: 'opportunity',
      stage: 4,
      lane: 1,
      title: '机会透镜：给小店做“上游基础设施”',
      summary: '当小店很难靠产品差异化，能降低采购、损耗与合规成本的服务就有价值。',
      detail:
        '机会包括：为区域小店提供集中采购与冷链、标准化耗材、门店选址与坪效诊断、食品安全培训与溯源、加盟商经营数据工具。不要只卖原料，要帮门店提高存活率与复购。',
      boundary: '低毛利、高履约成本，必须靠密度与复购；客户生命周期短，坏账和流失风险高。',
      evidence: 'inference',
      confidence: 'medium',
      sourceIds: ['mixue-prospectus'],
    },
  ],
  edges: [
    {
      id: 'mixue-e1',
      caseId: 'mixue',
      from: 'mixue',
      to: 'mixue-lowprice',
      label: '低价切入',
      explanation: '用低单价降低尝试门槛，换取高频消费与广泛客群。',
      tone: 'cause',
    },
    {
      id: 'mixue-e2',
      caseId: 'mixue',
      from: 'mixue',
      to: 'mixue-franchise',
      label: '加盟复制',
      explanation: '加盟者承担开店资本，总部用品牌与供应链快速扩张。',
      tone: 'cause',
    },
    {
      id: 'mixue-e3',
      caseId: 'mixue',
      from: 'mixue-lowprice',
      to: 'mixue-supplychain',
      label: '低价倒逼供应链',
      explanation: '要保持低价仍有利润，必须把采购、生产和物流成本压到足够低。',
      tone: 'cause',
    },
    {
      id: 'mixue-e4',
      caseId: 'mixue',
      from: 'mixue-franchise',
      to: 'mixue-supplychain',
      label: '门店变成采购网络',
      explanation: '数万家加盟店的原料、包装与设备需求，支撑总部自建供应链。',
      tone: 'cause',
    },
    {
      id: 'mixue-e5',
      caseId: 'mixue',
      from: 'mixue-franchise',
      to: 'mixue-density',
      label: '扩张提高密度',
      explanation: '加盟扩张在同一城市形成门店密度，降低物流与营销成本。',
      tone: 'cause',
    },
    {
      id: 'mixue-e6',
      caseId: 'mixue',
      from: 'mixue-supplychain',
      to: 'mixue-outcome',
      label: '上游利润',
      explanation: '总部通过向门店销售原料与设备获得收入，低价产品仍有利润空间。',
      tone: 'cause',
    },
    {
      id: 'mixue-e7',
      caseId: 'mixue',
      from: 'mixue-density',
      to: 'mixue-outcome',
      label: '网络认知',
      explanation: '高密度提高品牌曝光与购买便利，强化低价心智。',
      tone: 'cause',
    },
    {
      id: 'mixue-e8',
      caseId: 'mixue',
      from: 'mixue-condition',
      to: 'mixue-outcome',
      label: '市场与供应链前提',
      explanation: '低线城市需求与中国成熟供应链，让低价高频模型可行。',
      tone: 'enable',
    },
    {
      id: 'mixue-e9',
      caseId: 'mixue',
      from: 'mixue-outcome',
      to: 'mixue-risk',
      label: '规模制造治理难题',
      explanation: '门店越密、越多，分流、食安与加盟者回报问题越难被个案解决。',
      tone: 'risk',
    },
    {
      id: 'mixue-e10',
      caseId: 'mixue',
      from: 'mixue-risk',
      to: 'mixue-opp',
      label: '门店生存服务',
      explanation: '当单店竞争加剧，降低采购、损耗与合规成本的服务更有价值。',
      tone: 'enable',
    },
  ],
};
